import { useError } from "@/app-utils/graphql/useError.ts";
import {
  type GetContigFeatureSelectorStatisticsQueryVariables,
  type GetGenomeNucleotideStatisticsQueryVariables,
  type GetGenomeProteinStatisticsQueryVariables,
  type GetGenomeStatisticsQueryVariables,
  type GetGenomeVirtualGenomesStatisticsQueryVariables,
  type GetNucleotideFeatureSelectorStatisticsQueryVariables,
  useGetContigFeatureSelectorStatisticsQuery,
  useGetGenomeNucleotideStatisticsQuery,
  useGetGenomeProteinStatisticsQuery,
  useGetGenomeStatisticsQuery,
  useGetGenomeVirtualGenomesStatisticsQuery,
  useGetNucleotideFeatureSelectorStatisticsQuery,
} from "@/ui-modules/entities/genomes/graphql/genomes.gql.ts";

/**
 * Used in GenomicAnnotationsCard.tsx
 * @param variables
 */
export const useFetchGenomeAnnotationStatistics = (variables: GetGenomeStatisticsQueryVariables) => {
  // genomeId == 0 is not a real genome.
  const { data, loading, error } = useGetGenomeStatisticsQuery({
    variables,
    skip: !variables.genomeId,
    fetchPolicy: "cache-first", // use cached data instead of refetching. Refetch on page reload is sufficient
  });
  useError(error);
  return { data: data?.organism?.genomeStatistics, loading, error };
};

export const useFetchGenomeProteinStatistics = (variables: GetGenomeProteinStatisticsQueryVariables) => {
  // genomeId == 0 is not a real genome.
  const { data, loading, error } = useGetGenomeProteinStatisticsQuery({
    variables,
    skip: !variables.genomeId,
    fetchPolicy: "cache-first", // use cached data instead of refetching. Refetch on page reload is sufficient
  });
  useError(error);
  return { data: data?.organism?.genomeStatistics, loading, error };
};

export const useFetchGenomeNucleotideStatistics = (variables: GetGenomeNucleotideStatisticsQueryVariables) => {
  // genomeId == 0 is not a real genome.
  const { data, loading, error } = useGetGenomeNucleotideStatisticsQuery({
    variables,
    skip: !variables.genomeId,
    fetchPolicy: "cache-first", // use cached data instead of refetching. Refetch on page reload is sufficient
  });
  useError(error);
  return { data: data?.organism?.genomeStatistics, loading, error };
};

export const useFetchVirtualGenomesStatistics = (variables: GetGenomeVirtualGenomesStatisticsQueryVariables) => {
  // genomeId == 0 is not a real genome.
  const { data, loading, error } = useGetGenomeVirtualGenomesStatisticsQuery({
    variables,
    skip: !variables.genomeId,
    fetchPolicy: "cache-first", // use cached data instead of refetching. Refetch on page reload is sufficient
  });
  useError(error);
  return { data: data?.organism, loading, error };
};

export const useFetchNucleotideFeatureSelectorStatistics = (
  variables: GetNucleotideFeatureSelectorStatisticsQueryVariables,
) => {
  // genomeId == 0 is not a real genome.
  const { data, loading, error } = useGetNucleotideFeatureSelectorStatisticsQuery({
    variables,
    skip: !variables.genomeId,
    fetchPolicy: "cache-first", // use cached data instead of refetching. Refetch on page reload is sufficient
  });
  useError(error);
  return { data: data?.organism, loading, error };
};

export const useFetchContigFeatureSelectorStatistics = (
  variables: GetContigFeatureSelectorStatisticsQueryVariables,
) => {
  // genomeId == 0 is not a real genome.
  const { data, loading, error } = useGetContigFeatureSelectorStatisticsQuery({
    variables,
    skip: !variables.contigId,
    fetchPolicy: "cache-first", // use cached data instead of refetching. Refetch on page reload is sufficient
  });
  useError(error);
  return { data: data?.contig, loading, error };
};
